import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  Clock,
  Copy,
  Plus,
  PowerOff,
  Users,
  Wifi,
  type LucideIcon,
} from "lucide-react";
import { Button, Modal, SearchBar } from "@veorest/ui";
import {
  mapDriversRpcError,
  partnerDriverPresence,
  type PartnerDriverPresence,
  type PartnerDriverRow,
} from "../lib/partnerDrivers";
import { mapAccountError, type AccountCredentials } from "../lib/partnerAccounts";
import { driverDetailPath } from "../routes";
import {
  useActivatePartnerDriver,
  useCreatePartnerDriver,
  useCreateReadyPartnerDriver,
  useDeactivatePartnerDriver,
  usePartnerDriversList,
} from "../hooks/usePartnerDrivers";
import { usePartnerDriverWalletsV2 } from "../hooks/usePartnerWallets";
import { usePartnerProfile } from "../hooks/usePartnerSettings";
import { useI18n } from "../i18n";
import {
  DriverCreateModal,
  type DriverCreateValues,
} from "../components/drivers/DriverCreateModal";
import { CredentialsModal } from "../components/accounts/CredentialsModal";
import { DriversTable } from "../components/drivers/DriversTable";

type PresenceFilter = "all" | PartnerDriverPresence;

export default function DriversPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [presenceFilter, setPresenceFilter] = useState<PresenceFilter>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [passcodeBanner, setPasscodeBanner] = useState<string | null>(null);
  const [credentials, setCredentials] = useState<{
    title: string;
    data: AccountCredentials;
  } | null>(null);
  const [toggleTarget, setToggleTarget] = useState<PartnerDriverRow | null>(
    null
  );

  const driversQuery = usePartnerDriversList();
  const walletsQuery = usePartnerDriverWalletsV2();
  const createMutation = useCreatePartnerDriver();
  const readyCreateMutation = useCreateReadyPartnerDriver();
  const deactivateMutation = useDeactivatePartnerDriver();
  const activateMutation = useActivatePartnerDriver();
  const profileQuery = usePartnerProfile();

  const drivers = driversQuery.data ?? [];

  // Legacy ?id= deep links → the dedicated driver page.
  useEffect(() => {
    const fromUrl = searchParams.get("id");
    if (fromUrl) navigate(driverDetailPath(fromUrl), { replace: true });
  }, [searchParams, navigate]);

  const walletById = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of walletsQuery.data ?? []) {
      map.set(row.driver_id, row.balance);
    }
    return map;
  }, [walletsQuery.data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return drivers.filter((d) => {
      const active = d.is_active !== false;
      if (presenceFilter !== "all") {
        if (!active || partnerDriverPresence(d) !== presenceFilter) {
          return false;
        }
      }
      if (!q) return true;
      return (
        d.name.toLowerCase().includes(q) ||
        (d.phone ?? "").toLowerCase().includes(q)
      );
    });
  }, [drivers, search, presenceFilter]);

  const stats = useMemo(() => {
    let online = 0;
    let busy = 0;
    let offline = 0;
    for (const d of drivers) {
      if (d.is_active === false) continue;
      const p = partnerDriverPresence(d);
      if (p === "online") online += 1;
      else if (p === "busy") busy += 1;
      else offline += 1;
    }
    return { online, busy, offline, total: drivers.length };
  }, [drivers]);

  const statusTabs: {
    key: PresenceFilter;
    label: string;
    icon: LucideIcon;
    count: number;
  }[] = [
    { key: "all", label: t("analytics.all"), icon: Users, count: stats.total },
    {
      key: "online",
      label: t("drivers.presence.online"),
      icon: Wifi,
      count: stats.online,
    },
    {
      key: "busy",
      label: t("drivers.presence.busy"),
      icon: Clock,
      count: stats.busy,
    },
    {
      key: "offline",
      label: t("drivers.presence.offline"),
      icon: PowerOff,
      count: stats.offline,
    },
  ];

  const handleCreate = async (values: DriverCreateValues) => {
    try {
      if (values.mode === "ready") {
        const ready = await readyCreateMutation.mutateAsync({
          name: values.name,
          phone: values.phone,
          address: values.address,
          vehicleType: values.vehicleType,
          vehicleModel: values.vehicleModel,
          vehicleNumber: values.vehicleNumber,
          vehicleColor: values.vehicleColor,
          driverMode: values.driverMode,
          username: values.username,
          password: values.password,
        });
        toast.success(t("drivers.toast.created"));
        setCreateOpen(false);
        if (ready.driverId) navigate(driverDetailPath(ready.driverId));
        setCredentials({ title: values.name, data: ready.credentials });
        return;
      }

      // Existing passcode rail — unchanged.
      const result = await createMutation.mutateAsync({
        name: values.name,
        phone: values.phone,
        vehicleType: values.vehicleType,
        vehicleNumber: values.vehicleNumber.trim() || undefined,
        driverMode: values.driverMode,
      });
      toast.success(t("drivers.toast.created"));
      if (result.passcode) setPasscodeBanner(result.passcode);
      setCreateOpen(false);
      navigate(driverDetailPath(result.driver.id));
    } catch (err) {
      toast.error(
        t(
          values.mode === "ready"
            ? mapAccountError(err)
            : mapDriversRpcError(err)
        )
      );
    }
  };

  const handleToggle = (driver: PartnerDriverRow) => {
    if (driver.is_active === false) {
      activateMutation.mutate(driver.id, {
        onSuccess: () => toast.success(t("drivers.toast.activated")),
        onError: (err) => toast.error(t(mapDriversRpcError(err))),
      });
      return;
    }
    setToggleTarget(driver);
  };

  const confirmDeactivate = async () => {
    if (!toggleTarget) return;
    try {
      await deactivateMutation.mutateAsync(toggleTarget.id);
      toast.success(t("drivers.toast.deactivated"));
      setToggleTarget(null);
    } catch (err) {
      toast.error(t(mapDriversRpcError(err)));
    }
  };

  return (
    <div className="menu-page menu-page--wide">
      <header className="menu-page__header overview-page__header">
        <div className="min-w-0">
          <p className="menu-page__eyebrow">{t("nav.drivers")}</p>
          <h1 className="menu-page__title overview-page__title">
            {t("drivers.title")}
            <span className="orders-head__count">{stats.total}</span>
          </h1>
          <p className="menu-page__subtitle overview-page__subtitle">
            {t("drivers.subtitle")}
          </p>
        </div>
        <div className="menu-page__header-actions overview-page__header-actions">
          <div className="overview-date-live">
            <span className="overview-date-live__dot" aria-hidden />
            {t("overview.updatedLive")}
          </div>
          <Button
            variant="primary"
            onClick={() => setCreateOpen(true)}
            className="min-h-10 gap-2 px-4"
          >
            <Plus className="h-4 w-4" />
            {t("drivers.add")}
          </Button>
        </div>
      </header>

      {passcodeBanner ? (
        <div className="rounded-xl border border-[var(--accent)]/30 bg-[var(--accent-light)] px-4 py-3 text-sm">
          <p className="font-bold text-[var(--text-primary)]">{t("drivers.passcode")}</p>
          <p className="mt-1 font-mono text-lg font-black tracking-widest text-[var(--accent)]">
            {passcodeBanner}
          </p>
          <div className="mt-2 flex items-center gap-4">
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs font-bold text-[var(--accent)]"
              onClick={() => {
                void navigator.clipboard
                  .writeText(passcodeBanner)
                  .then(() => toast.success(t("common.copied")))
                  .catch(() => toast.error(t("common.copyFailed")));
              }}
            >
              <Copy className="h-3.5 w-3.5" />
              {t("common.copy")}
            </button>
            <button
              type="button"
              className="text-xs font-bold text-[var(--accent)]"
              onClick={() => setPasscodeBanner(null)}
            >
              {t("clients.dismiss")}
            </button>
          </div>
        </div>
      ) : null}

      <div className="filter-bar">
        <SearchBar
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("drivers.searchPlaceholder")}
          aria-label={t("drivers.searchAria")}
          className="max-w-xs"
        />
        <div
          className="orders-step-strip filter-strip"
          role="tablist"
          aria-label={t("drivers.filterAll")}
        >
          {statusTabs.map(({ key, label, icon: Icon, count }) => {
            const active = presenceFilter === key;
            return (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={active}
                className={`orders-step-chip ${
                  active ? "orders-step-chip--active" : ""
                }`}
                onClick={() => setPresenceFilter(key)}
              >
                <span className="orders-step-chip__icon" aria-hidden>
                  <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
                </span>
                <span className="orders-step-chip__label">{label}</span>
                <span
                  className={`orders-step-chip__count ${
                    count === 0 ? "orders-step-chip__count--muted" : ""
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <section className="menu-panel">
        <div className="menu-panel__body">
          <DriversTable
            drivers={filtered}
            loading={driversQuery.isLoading}
            walletById={walletById}
            defaultPay={profileQuery.data?.driver_trip_fee_default ?? null}
            onOpen={(driver) => navigate(driverDetailPath(driver.id))}
            onToggle={handleToggle}
          />
        </div>
      </section>

      <Modal
        open={!!toggleTarget}
        onClose={() => {
          if (!deactivateMutation.isPending) setToggleTarget(null);
        }}
        panelClassName="max-w-md"
      >
        <div className="px-5 py-4">
          <h2 className="text-lg font-extrabold text-[var(--text-primary)]">
            {t("drivers.deactivateTitle")}
          </h2>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            {t("drivers.deactivateBody")}
          </p>
          <p className="mt-2 text-sm font-bold text-[var(--text-primary)]">
            {toggleTarget?.name}
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="btn-secondary min-h-[44px] flex-1"
              onClick={() => setToggleTarget(null)}
              disabled={deactivateMutation.isPending}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="btn-danger min-h-[44px] flex-1"
              onClick={() => void confirmDeactivate()}
              disabled={deactivateMutation.isPending}
            >
              {deactivateMutation.isPending
                ? t("common.loading")
                : t("drivers.deactivate")}
            </button>
          </div>
        </div>
      </Modal>

      <DriverCreateModal
        open={createOpen}
        busy={createMutation.isPending || readyCreateMutation.isPending}
        defaultMode="paid"
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
      />

      <CredentialsModal
        open={!!credentials}
        title={credentials?.title ?? ""}
        subtitle={t("drivers.readyCredentialsHint")}
        credentials={credentials?.data ?? null}
        onClose={() => setCredentials(null)}
      />
    </div>
  );
}
